import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { sendContractTransaction, approveToken, formatTransactionError } from '../utils/transactionHelper';
import './CreateSellOrder.css';

const CreateSellOrder = () => {
  const web3Context = useWeb3();
  const { account, contract, web3, tokenContract, CONTRACT_ADDRESS, TOKEN_ADDRESS } = web3Context;

  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenBalance, setTokenBalance] = useState('0');
  const [minSellAmount, setMinSellAmount] = useState('0');
  const [tokenPrice, setTokenPrice] = useState('0');
  const [offerDetails, setOfferDetails] = useState({ bonus: '0', fee: '0' });
  const [activeReferrals, setActiveReferrals] = useState('0');
  const [totalSellOrders, setTotalSellOrders] = useState('0');

  useEffect(() => {
    if (account && contract && tokenContract) {
      loadData();
    }
  }, [account, contract, tokenContract]);

  useEffect(() => {
    if (amount && contract && web3) {
      calculateOffer();
    } else {
      setOfferDetails({ bonus: '0', fee: '0' });
    }
  }, [amount, contract, web3]);

  const loadData = async () => {
    try {
      // Get token balance
      const balance = await tokenContract.methods.balanceOf(account).call();
      setTokenBalance(web3.utils.fromWei(balance, 'ether'));

      // Get minimum sell amount
      const minSell = await contract.methods.minSellAmount().call();
      setMinSellAmount(web3.utils.fromWei(minSell, 'ether'));

      // Get token price
      const price = await contract.methods.tokenPerUSDT().call();
      setTokenPrice(price);

      // Get trade stats for referral check
      const tradeStats = await contract.methods.getTradeStats(account).call();
      setActiveReferrals(tradeStats.activeRefs.toString());
      setTotalSellOrders(tradeStats.sellCount.toString());
    } catch (error) {
      console.error('Error loading sell data:', error);
    }
  };

  const calculateOffer = async () => {
    try {
      const amtNum = parseFloat(amount);
      if (isNaN(amtNum) || amtNum <= 0) {
        setOfferDetails({ bonus: '0', fee: '0' });
        return;
      }

      const amountWei = web3.utils.toWei(amount, 'ether');
      const offer = await contract.methods.getOffer(amountWei).call();
      setOfferDetails({
        bonus: offer.bonus.toString(),
        fee: web3.utils.fromWei(offer.fee.toString(), 'ether')
      });
    } catch (error) {
      console.error('Error calculating offer:', error);
    }
  };

  const handleCreateSellOrder = async () => {
    if (!account || !contract) {
      alert('Please connect your Web3 wallet');
      return;
    }

    const amtNum = parseFloat(amount);
    if (isNaN(amtNum) || amtNum <= 0) {
      alert('Please enter a valid positive token amount');
      return;
    }

    if (amtNum < parseFloat(minSellAmount)) {
      alert(`Minimum sell amount requirement is ${minSellAmount} Tokens`);
      return;
    }

    if (amtNum > parseFloat(tokenBalance)) {
      alert(`Insufficient balance! You have ${tokenBalance} Tokens.`);
      return;
    }

    // Referral requirement: if activeReferrals == 0, user can create only 1 sell order until activating a referral
    if (activeReferrals === '0' && parseInt(totalSellOrders) >= 1) {
      alert('You need at least 1 active referral to create additional sell orders!');
      return;
    }

    setLoading(true);
    try {
      const amountWei = web3.utils.toWei(amount, 'ether');

      // Step 1: Approve Token transfer to contract
      await approveToken(web3Context, TOKEN_ADDRESS, CONTRACT_ADDRESS, amountWei);

      // Step 2: Create sell order with feeBNB value
      const feeWei = web3.utils.toWei(offerDetails.fee || '0', 'ether');
      const method = contract.methods.createSellOrder(amountWei);
      const options = { value: feeWei };

      await sendContractTransaction(web3Context, method, options);

      alert(`Successfully created sell order for ${amount} Tokens!`);
      setAmount('');
      loadData();
    } catch (error) {
      console.error('Error creating sell order:', error);
      const errorMessage = formatTransactionError(error);
      alert('Failed to create sell order: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const calculateUSDTValue = () => {
    if (!amount || !tokenPrice || tokenPrice === '0' || isNaN(parseFloat(amount))) return '0.00';
    const usdtValue = parseFloat(amount) / parseFloat(tokenPrice);
    return usdtValue.toFixed(4);
  };

  const setPercentage = (pct) => {
    const maxVal = parseFloat(tokenBalance);
    const calculated = (maxVal * (pct / 100)).toFixed(4);
    setAmount(calculated);
  };

  const isReferralLocked = activeReferrals === '0' && parseInt(totalSellOrders) >= 1;

  return (
    <div className="create-sell-wrapper">
      <div className="create-sell-card">
        <div className="card-header-banner">
          <h2>Create P2P Sell Order</h2>
          <p>Deposit tokens into escrow and list your sell order on the open P2P marketplace.</p>
        </div>

        {/* User Status Bar */}
        <div className="status-grid">
          <div className="status-item">
            <span className="label">Available Tokens</span>
            <span className="val highlight">{parseFloat(tokenBalance).toLocaleString()} Tokens</span>
          </div>
          <div className="status-item">
            <span className="label">Min Order Requirement</span>
            <span className="val">{parseFloat(minSellAmount).toLocaleString()} Tokens</span>
          </div>
          <div className="status-item">
            <span className="label">Active Referrals</span>
            <span className={`val ${isReferralLocked ? 'locked' : 'unlocked'}`}>
              {activeReferrals} Active
            </span>
          </div>
        </div>

        {/* Referral Restriction Alert */}
        {isReferralLocked && (
          <div className="alert-box error-alert">
            ⚠️ <strong>Referral Requirement Triggered:</strong> You have reached your limit of 1 sell order. Share your referral link and onboard an active trader to unlock unlimited sell orders!
          </div>
        )}

        {/* Form Controls */}
        <div className="form-section">
          <div className="input-group">
            <label>Token Amount to Escrow & Sell</label>
            <div className="input-container">
              <input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={loading || isReferralLocked}
                min={minSellAmount}
                max={tokenBalance}
              />
              <button
                className="max-chip-btn"
                onClick={() => setAmount(tokenBalance)}
                disabled={loading || isReferralLocked}
              >
                MAX
              </button>
            </div>

            {/* Quick % buttons */}
            <div className="pct-row">
              <button onClick={() => setPercentage(25)} disabled={loading || isReferralLocked}>25%</button>
              <button onClick={() => setPercentage(50)} disabled={loading || isReferralLocked}>50%</button>
              <button onClick={() => setPercentage(75)} disabled={loading || isReferralLocked}>75%</button>
              <button onClick={() => setPercentage(100)} disabled={loading || isReferralLocked}>100%</button>
            </div>
          </div>

          {/* Dynamic Order Estimation Box */}
          {amount && parseFloat(amount) > 0 && (
            <div className="order-summary-box">
              <h4>Order Breakdown & Incentives</h4>
              <div className="summary-line">
                <span>Escrow Token Amount:</span>
                <span>{parseFloat(amount).toLocaleString()} Tokens</span>
              </div>
              <div className="summary-line">
                <span>Estimated USDT Return:</span>
                <span className="usdt-val">{calculateUSDTValue()} USDT</span>
              </div>
              <div className="summary-line">
                <span>Exchange Rate:</span>
                <span>1 USDT = {tokenPrice} Tokens</span>
              </div>
              <div className="summary-line">
                <span>Buyer Bonus Incentive:</span>
                <span className="bonus-val">+{offerDetails.bonus}% Bonus</span>
              </div>
              <div className="summary-line fee-line">
                <span>Platform BNB Fee:</span>
                <span className="fee-val">{offerDetails.fee} BNB</span>
              </div>
            </div>
          )}

          {/* Create Button */}
          <button
            className="btn-create-sell"
            onClick={handleCreateSellOrder}
            disabled={loading || !amount || parseFloat(amount) <= 0 || isReferralLocked}
          >
            {loading ? (
              <span className="spinner">Processing Token Approval & Order Creation...</span>
            ) : isReferralLocked ? (
              'Referral Activation Required'
            ) : (
              'Approve Token & Publish Sell Order'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSellOrder;